// Copyright 2026, Imprivata, Inc.  All rights reserved.

import '@imprivata-cloud/eamc-chatbot/dist/style.css';
import { EamcChatbot } from '@imprivata-cloud/eamc-chatbot';
import type { TelemetryEvent } from '@imprivata-cloud/eamc-chatbot';
import { useState } from 'react';
import { useSession } from '@common/authorization/context/Session';
import { Entitlement, SearchParams } from '@api/constants';
import { hasEntitlement, getTenantIdFromUrl } from '@api/utils';
import { ROLE_TYPE_ADMIN } from '@portal/constants';
import { featureSupported } from '../featureFlagsUtils';
import { EamcChatbotSideDock } from './EamcChatbotSideDock';
import { CHATBOT_FEATURE_FLAG, getChatbotBffBaseUrl } from './config';
import { chatbotAuthFetcher } from './authFetcher';
import { forwardTelemetryToPendo } from './telemetry';

/**
 * Gating component — composes the three checks.
 *
 * Returns `null` when ANY condition fails:
 *   (a) ENABLE_EAMC_CHATBOT feature flag is off
 *   (b) tenant does not hold Entitlement.SSO
 *   (c) current user's roleType does not match ROLE_TYPE_ADMIN (case-insensitive)
 *
 * When the gate fails: no DOM, no telemetry, no network call .
 *
 * Open/close state lives here because the upstream EamcChatbot component is
 * stateless with respect to visibility — its X button fires a `chat_panel_closed`
 * telemetry event and expects the host to unmount or hide the panel.
 */
export const EamcChatbotGate = () => {
  const { tenantInfo, userInfo } = useSession();
  // Derive tenant ID from the URL the same way fetcher does — the subdomain
  // prefix (e.g. "mytenant" in mytenant.access.dev.imprivata.cloud) is what
  // _getUrl needs to construct the BFF subdomain, not the server-returned ID.
  const tenantId =
    getTenantIdFromUrl(globalThis.location.hostname)?.tenantId ??
    new URLSearchParams(globalThis.location.search).get(SearchParams.TENANT_ID);
  // isOpen must be declared before any early return (Rules of Hooks).
  // Default false — the panel is closed on mount; user clicks the launcher FAB to open.
  const [isOpen, setIsOpen] = useState(false);

  const featureOn = featureSupported(CHATBOT_FEATURE_FLAG);
  const hasSso = hasEntitlement(tenantInfo, Entitlement.SSO);
  const isAdmin = (userInfo?.roleType ?? '').toLowerCase() === ROLE_TYPE_ADMIN;

  if (!(featureOn && hasSso && isAdmin)) {
    return null;
  }

  const apiBaseUrl = getChatbotBffBaseUrl(tenantId);

  const handleTelemetry = (event: TelemetryEvent) => {
    // The upstream component signals close via telemetry; the host owns visibility.
    if (event.type === 'chat_panel_closed') {
      setIsOpen(false);
    }
    forwardTelemetryToPendo(event);
  };

  return (
    <EamcChatbotSideDock isOpen={isOpen} onOpen={() => setIsOpen(true)}>
      <EamcChatbot
        apiBaseUrl={apiBaseUrl}
        authFetcher={chatbotAuthFetcher}
        onTelemetry={handleTelemetry}
        showExpandControl
      />
    </EamcChatbotSideDock>
  );
};
